import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '@/components/Footer';
import PageWrapper from '@/components/PageWrapper';
import ErrorLine from '@/components/ErrorLine';
import { useRegister } from '@/hooks/api/useAuth';

const primary = 'var(--primary-base)';
const greyBg = 'var(--surface-surface_2)';
const greyText = 'var(--text-low_em)';
const highText = 'var(--text-high_em)';
const errorCol = 'var(--error-color)';

const RegisterPage: React.FC = () => {
  const registerMutation = useRegister();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username || username.length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Invalid email');
      return;
    }
    if (!agree) {
      setError('You must accept the terms and conditions');
      return;
    }

    setLoading(true);
    try {
      await registerMutation.mutateAsync({
        username,
        email,
        password: '',
        password_confirm: '',
      });
      setDone(true);
    } catch (e: unknown) {
      setError((e as Error)?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <main
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--surface-surface_0)',
        }}
      >
        <PageWrapper>
          <form
            onSubmit={handleSubmit}
            style={{
              width: 480,
              padding: '48px 32px',
              borderRadius: 24,
              background: 'var(--surface-surface_0)',
              border: '1px solid var(--outline-base_em, #F4F4F6)',
              boxShadow:
                '0px 1px 1px -0.5px var(--elevationshadow), 0px 3px 3px -1.5px var(--elevationshadow), 0px 24px 24px -12px var(--elevationshadow)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 24,
            }}
          >
            <h1
              style={{
                fontFamily: 'Coolvetica, sans-serif',
                fontSize: 64,
                lineHeight: '48px',
                color: primary,
                margin: 0,
              }}
            >
              swarmind
            </h1>

            <h2 style={{ margin: 0 }}>Create account</h2>

            {done ? (
              <div style={{ width: '100%', textAlign: 'center', color: greyText }}>
                We sent an activation link to your email. Open the link and set your password.
              </div>
            ) : (
              <>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: 13, fontWeight: 500, color: greyText }}>Username</label>
                  <input
                    type="text"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    placeholder="Enter username"
                    autoComplete="username"
                    style={{
                      width: '100%',
                      height: 48,
                      borderRadius: 12,
                      background: greyBg,
                      color: highText,
                      WebkitTextFillColor: highText,
                      border: `1px solid ${error ? errorCol : 'transparent'}`,
                      padding: '0 12px',
                      fontSize: 15,
                      boxSizing: 'border-box',
                      outline: 'none',
                    }}
                  />
                </div>

                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: 13, fontWeight: 500, color: greyText }}>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Enter email"
                    autoComplete="email"
                    style={{
                      width: '100%',
                      height: 48,
                      borderRadius: 12,
                      background: greyBg,
                      color: highText,
                      WebkitTextFillColor: highText,
                      border: `1px solid ${error ? errorCol : 'transparent'}`,
                      padding: '0 12px',
                      fontSize: 15,
                      boxSizing: 'border-box',
                      outline: 'none',
                    }}
                  />
                </div>

                <label style={{ width: '100%', display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: greyText }}>
                  <input
                    type="checkbox"
                    checked={agree}
                    onChange={e => setAgree(e.target.checked)}
                  />
                  I agree to terms and conditions
                </label>

                {error && <ErrorLine>{error}</ErrorLine>}

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%',
                    height: 48,
                    borderRadius: 12,
                    background: loading ? greyBg : primary,
                    color: loading ? greyText : '#fff',
                    border: 'none',
                    fontWeight: 600,
                    fontSize: 15,
                    cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? 'Sending...' : 'Continue'}
                </button>
              </>
            )}

            <Link to="/login" style={{ color: primary, fontSize: 14, textDecoration: 'none' }}>
              Back to login
            </Link>
          </form>
        </PageWrapper>
      </main>
      <Footer />
    </div>
  );
};

export default RegisterPage;
